import { OrderItem } from "@/hooks/useOrderCalculations";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { Order, ScheduledOrder } from "./api";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Séries (etapas): cada variante ocupa um bloco de 19 códigos consecutivos,
// na ordem de SERIES_STAGES (base 1–19, Bilíngue 20–38, Upsell 39–57, ...).
// Nova variante: adicione o sufixo ao FINAL da lista — reordenar muda os códigos.
const SERIES_VARIANT_SUFFIXES = [
  "",
  " - Bilíngue",
  " - Upsell",
  " - Aprovação PHD", // 58–76
  " - Colégio Sol", // 77–95
  " - Renascer", // 96–114
  " - Travessia", // 115–133
  " - Alpha", // 134–152
] as const;

const INFANTIL_SEGMENT = "Ed. Infantil";

type SeriesStage = { name: string; segment: string };

const stagesOf = (segment: string, names: string[]): SeriesStage[] =>
  names.map((name) => ({ name, segment }));

const SERIES_STAGES: readonly SeriesStage[] = [
  ...stagesOf(INFANTIL_SEGMENT, ["Grupo 1", "Grupo 2", "Grupo 3", "Grupo 4", "Grupo 5"]),
  ...stagesOf("Fund. Anos Iniciais", ["1º ano", "2º ano", "3º ano", "4º ano", "5º ano"]),
  ...stagesOf("Fund. Anos Finais", ["6º ano", "7º ano", "8º ano", "9º ano"]),
  ...stagesOf("Ensino Médio", ["1ª série", "2ª série", "3ª série"]),
  ...stagesOf("PV", ["Semi", "Extensivo"]),
];

// Código 0 (Berçário) fica fora dos blocos; os blocos começam em 1.
const SERIES_BY_CODE = new Map<number, SeriesStage>(
  SERIES_VARIANT_SUFFIXES.flatMap((suffix, variantIndex) =>
    SERIES_STAGES.map((stage, stageIndex): [number, SeriesStage] => [
      1 + variantIndex * SERIES_STAGES.length + stageIndex,
      { name: `${stage.name}${suffix}`, segment: stage.segment },
    ]),
  ),
);

const SERIES_STAGE_BY_NAME = new Map(
  Array.from(SERIES_BY_CODE.values(), (stage) => [stage.name.toLowerCase(), stage]),
);

const parseEtapa = (serie: string | number | null | undefined): number => {
  if (serie === null || serie === undefined) return NaN;
  const value = String(serie).trim();
  return /^\d+$/.test(value) ? Number(value) : NaN;
};

const resolveSeriesStage = (
  serie: string | number | null | undefined,
): SeriesStage | undefined => {
  if (serie === null || serie === undefined) return undefined;

  const value = String(serie).trim();
  if (!value) return undefined;

  const code = parseEtapa(value);
  if (code === 0) return { name: "Berçário", segment: INFANTIL_SEGMENT };
  if (Number.isInteger(code) && code > 0) {
    return SERIES_BY_CODE.get(code) ?? SERIES_STAGES[(code - 1) % SERIES_STAGES.length];
  }

  return SERIES_STAGE_BY_NAME.get(value.toLowerCase());
};

// Map serie (etapa) to educational segment
export const mapEtapaToSegment = (serie: string | number | null): string =>
  resolveSeriesStage(serie)?.segment ?? "Outros";

export const STUDENTS_REQUIRED_FOR_BONUS_INFANTIL = 11;
export const STUDENTS_REQUIRED_FOR_BONUS_DEFAULT = 20;

// Divisor da bonificação Professor/Aluno: usa o valor do backend quando presente
// (preserva acordos contratuais por cliente); na ausência, aplica a regra padrão
// por segmento (1:11 Ensino Infantil, 1:20 demais).
export const resolveRequiredForBonus = (
  professorItem: { serie?: string | null; quantity_required_for_bonus?: number | null },
): number => {
  if (professorItem.quantity_required_for_bonus) {
    return professorItem.quantity_required_for_bonus;
  }
  return mapEtapaToSegment(professorItem.serie) === INFANTIL_SEGMENT
    ? STUDENTS_REQUIRED_FOR_BONUS_INFANTIL
    : STUDENTS_REQUIRED_FOR_BONUS_DEFAULT;
};

// Item mínimo necessário para calcular bonificação Professor/Aluno — tanto
// OrderInterface/types.ts quanto Simulation2027.tsx usam interfaces locais
// compatíveis estruturalmente com esta.
export interface BonusableOrderItem {
  id: number;
  product_id?: number;
  utilizacao?: string | null;
  serie?: string | null;
  quantity_required_for_bonus?: number | null;
  is_printed_product?: boolean;
  related_product_ids?: number[];
  ordered_quantity?: number;
  quantidade_pedido: number;
  used_bonus_quantity?: number;
  valor_liquido: number;
  valor_total: number;
  bonified_quantity?: number;
  available_bonus_quantity?: number;
  earned_bonus_quantity?: number;
  codigo_produto?: string;
  descricao_produto?: string;
}

export const calculateEarnedBonusQuantity = (
  studentQuantity: number,
  requiredStudentQuantity: number,
): number => {
  if (studentQuantity <= 0 || requiredStudentQuantity <= 0) return 0;

  return Math.ceil(studentQuantity / requiredStudentQuantity);
};

const PRODUCT_BONUS_EXCLUDED_DESCRIPTIONS = ["elt", "ai4school"];

export const isProductEligibleForBonus = (description?: string | null): boolean => {
  const normalizedDescription = description?.toLocaleLowerCase("pt-BR") ?? "";

  return !PRODUCT_BONUS_EXCLUDED_DESCRIPTIONS.some(excludedDescription =>
    normalizedDescription.includes(excludedDescription)
  );
};

// Recalcula bonified_quantity/available_bonus_quantity/earned_bonus_quantity/
// valor_total para cada item Professor com base na quantidade pedida dos
// produtos de Aluno relacionados (related_product_ids). A quantidade própria
// do item Professor (quantidade_pedido) NÃO é alterada aqui — ela permanece
// sob controle manual do usuário; o que muda é apenas quanto dela é
// reconhecido como bonificado.
// earned_bonus_quantity = total de blocos ganhos até agora (Math.ceil),
// independente de consumo — é o "total" (equivalente ao bonifiedQuantity do
// backend). available_bonus_quantity = earned_bonus_quantity - used_bonus_quantity,
// ou seja, o saldo ainda não consumido.
export const applyProductBonuses = <T extends BonusableOrderItem>(
  items: T[],
  isBonusEnabled: boolean,
): T[] => items.map(item => {
  if (!isBonusEnabled) {
    return {
      ...item,
      bonified_quantity: 0,
      valor_total: Math.round(item.valor_liquido * item.quantidade_pedido * 100) / 100,
    };
  }

  if (item.utilizacao !== "Professor" || !item.is_printed_product) {
    return { ...item, bonified_quantity: 0 };
  }

  if (!isProductEligibleForBonus(item.descricao_produto)) {
    return {
      ...item,
      bonified_quantity: 0,
      available_bonus_quantity: 0,
      earned_bonus_quantity: 0,
      valor_total: Math.round(item.valor_liquido * item.quantidade_pedido * 100) / 100,
    };
  }

  const studentQuantity = items
    .filter(candidate =>
      candidate.is_printed_product
      && (item.related_product_ids ?? []).includes(candidate.product_id ?? candidate.id)
    )
    .reduce((total, student) => total + (student.ordered_quantity ?? 0) + student.quantidade_pedido, 0);
  const requiredForBonus = resolveRequiredForBonus(item);
  const earned = calculateEarnedBonusQuantity(studentQuantity, requiredForBonus);
  const available = Math.max(earned - (item.used_bonus_quantity ?? 0), 0);
  const bonifiedQuantity = Math.min(item.quantidade_pedido, available);
  const paidQuantity = item.quantidade_pedido - bonifiedQuantity;

  return {
    ...item,
    bonified_quantity: bonifiedQuantity,
    available_bonus_quantity: available,
    earned_bonus_quantity: earned,
    valor_total: Math.round(item.valor_liquido * paidQuantity * 100) / 100,
  };
});

const seriesMap: Record<number, string> = {
  0: "Berçário",
  ...Object.fromEntries(Array.from(SERIES_BY_CODE, ([code, { name }]) => [code, name])),
};

const SERIES_CODE_BY_NAME = new Map(
  Object.entries(seriesMap).map(([code, name]) => [name.toLowerCase(), Number(code)]),
);

export const marcasMap: Record<number, string> = {
  1: "AZ",
  2: "High Five",
  3: "My Life",
  4: "LEX",
  5: "AI4SCHOOL",
}
export const getMarca = (marca: number | string) => {
  return marcasMap[Number(marca)] ?? "Outras";
}

export const decodeSeries = (code: number): string => {
  return seriesMap[code] ?? "Série não encontrada";
};

export const codeSeries = (name: string | number | null | undefined): number => {
  if (name === null || name === undefined) {
    return 999;
  }
  const cleanName = String(name).trim().toLowerCase();

  // Handle special cases not in seriesMap
  if (cleanName.includes("berçário")) return -2; // Assign a low number to sort first

  // Antes do teste "infantil": nome de série conhecida tem precedência sobre o teste genérico.
  const code = SERIES_CODE_BY_NAME.get(cleanName);
  if (code !== undefined) return code;

  if (cleanName.includes("infantil")) return -1; // Assign a low number to sort second

  return 999;
};

// Map serie (etapa) to proper grade level name
export const mapEtapaToGradeName = (serie: string | number | null): string =>
  resolveSeriesStage(serie)?.name
  ?? (typeof serie === "string" && serie.trim() ? serie.trim() : "Outros");

export const isSecondSemesterProduct = (description: string | null | undefined): boolean => {
  if (!description) return false;
  const normalized = description
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  return /(?:2\s*[º°o]|segundo)\s*semestre/.test(normalized);
};

/** Bucket de prioridade do cupom: imediato (anual/semestral) antes de programado (2º sem./avaliação). */
export const couponBucketForItem = (
  item: { delivery_type_number?: number | null; scheduling_category?: string | null; descricao_produto?: string | null },
  cycleYear: number | null | undefined,
  deferred: boolean,
): "Annual" | "Semestral" | "SecondSemester" | "Exam" => {
  if (deferred && isDeferredProgrammedOrderItem(item, cycleYear)) {
    return item.scheduling_category === "ExamScheduled" ? "Exam" : "SecondSemester";
  }
  return item.delivery_type_number === 1 ? "Annual" : "Semestral";
};

export const isProgrammedOrderItem = (
  item: { descricao_produto?: string | null; scheduling_category?: string | null },
  cycleYear?: number | null,
): boolean => {
  const isNewFlow = !!item.scheduling_category
    || (cycleYear != null && cycleYear >= 2027);

  if (isNewFlow) {
    // ImmediateAndSecondSemester é o produto cobrado no pedido atual que gera
    // outro item programado: a decisão é do backend e ele nunca é programado direto.
    if (item.scheduling_category === "ImmediateAndSecondSemester") return false;
    // O backend classificou o item como programado (de-para / composição de exames).
    // ImmediateAndExam (Avaliações Nacionais / Simulado ENEM) pertence aos DOIS lados: é cobrado agora
    // no pedido normal E gera o item programado com a mesma quantidade. Para "o que é cobrado agora"
    // use isDeferredProgrammedOrderItem; esta função diz o que aparece como programado.
    if (item.scheduling_category === "SecondSemesterOnly"
      || item.scheduling_category === "ExamScheduled"
      || item.scheduling_category === "ImmediateAndExam") {
      return true;
    }
    // Ciclos 2027+: a decisão é exclusivamente do backend (flag/categoria enviadas na lista
    // de produtos). Immediate, Unresolved ou ausente = item de pedido normal.
    return false;
  }

  // Ciclos legados (<= 2026): comportamento anterior, somente 2º semestre.
  return isSecondSemesterProduct(item.descricao_produto);
};

/**
 * Na lista "Pedido programado" (resumo do checkout e PDF) só os itens de 2º semestre mostram valor.
 * Avaliações e simulados programados (ExamScheduled / ImmediateAndExam) seguem sem valor na lista.
 */
export const showsProgrammedListValue = (
  item: { scheduling_category?: string | null },
): boolean => item.scheduling_category !== "ExamScheduled"
  && item.scheduling_category !== "ImmediateAndExam";

/**
 * Avaliação/simulado programado (ExamScheduled / ImmediateAndExam). O cupom por item não aceita esses produtos; o
 * 2º semestre (SecondSemesterOnly) é aceito e o desconto é abatido na cobrança do pedido programado.
 */
export const isExamProgrammedOrderItem = (
  item: { scheduling_category?: string | null },
): boolean => item.scheduling_category === "ExamScheduled"
  || item.scheduling_category === "ImmediateAndExam";

/**
 * Item programado cuja cobrança fica para a confirmação (2º semestre / avaliação somente programada).
 * Exclui ImmediateAndExam, que é cobrado agora no pedido normal além de gerar o programado.
 */
export const isDeferredProgrammedOrderItem = (
  item: { descricao_produto?: string | null; scheduling_category?: string | null },
  cycleYear?: number | null,
): boolean => item.scheduling_category !== "ImmediateAndExam"
  && isProgrammedOrderItem(item, cycleYear);

/**
 * O item pertence ao pedido do modo atual? Pedido normal (isScheduled = false): tudo que é cobrado agora
 * (inclui ImmediateAndExam). Pedido programado/simulado (isScheduled = true): os itens programados
 * (inclui ImmediateAndExam, como antes acontecia com ExamScheduled).
 */
export const belongsToOrderMode = (
  item: { descricao_produto?: string | null; scheduling_category?: string | null },
  isScheduled: boolean,
  cycleYear?: number | null,
): boolean => isScheduled
  ? isProgrammedOrderItem(item, cycleYear)
  : !isDeferredProgrammedOrderItem(item, cycleYear);

export const isExamScheduledOrderItem = (
  item: { scheduling_category?: string | null },
  cycleYear?: number | null,
): boolean => cycleYear != null && cycleYear >= 2027
  && item.scheduling_category === "ExamScheduled";

// Produto opcional (profile_client.is_optional): fica fora da quantidade da série — não define a
// "Qtd. Referência" nem recebe a atualização em lote; série só com opcionais é atualizada normalmente.
export const getSeriesBatchItems = <T extends { is_optional?: boolean }>(seriesItems: T[]): T[] => {
  const mandatory = seriesItems.filter(item => !item.is_optional);
  return mandatory.length > 0 ? mandatory : seriesItems;
};

export const getDeliveryTypeTotalUtil = (
  orderItems: OrderItem[],
  deliveryTypeNumber: number,
  isScheduled: boolean,
) => {
  return orderItems
    .filter((item) => {
      const isMatch =
        item.delivery_type_number === deliveryTypeNumber ||
        item.delivery_type_id === deliveryTypeNumber;
      if (!isMatch || item.quantidade_pedido <= 0) {
        return false;
      }

      // const isSecondSemester = item.descricao_produto.includes("2º Semestre");
      // if (!isScheduled && isSecondSemester) return false;

      return true;
    })
    .reduce((total, item) => total + item.valor_total, 0);
};

export const transformScheduledToOrder = (originalData: any): Order => {
  try {
    // 1. Faz o parse da string JSON que está dentro de orderPayload
    const payload =
      typeof originalData.orderPayload === "string"
        ? JSON.parse(originalData.orderPayload)
        : originalData.orderPayload;

    if (!payload || !payload.Customer) {
      console.error("Payload de pedido programado inválido:", originalData);
      return {
        id: originalData.id,
        orderNumber: originalData.orderNumber,
        clientName: originalData.clientName,
        clientCode: originalData.clientCode,
        status: originalData.status,
        portalB2BOrderStatus: "",
        createdAt: originalData.createdAt,
        updatedAt: originalData.updatedAt,
        totalAmount: 0,
        shippingAmount: 0,
        customer: {
          clientId: parseInt(originalData.clientId),
        } as any,
        items: [],
        isScheduled: true,
      } as any;
    }

    // 2. Mapeia os itens para o novo formato
    const items = (payload.Items || []).map((item: any) => ({
      id: crypto.randomUUID(), // Gerando um ID temporário já que o payload original não tem
      academicYear: item.AcademicYear || null,
      brand: item.Brand || null,
      contractualDiscount: item.ContractualDiscount,
      createdAt: originalData.createdAt,
      discountAmount: item.DiscountAmount,
      educationLevel: item.EducationLevel || null,
      itemTotal: item.ItemTotal,
      netPrice: item.NetPrice,
      productCode: item.ProductCode,
      productDescription: item.ProductDescription,
      productSeries: item.ProductSeries ? String(item.ProductSeries).trim() : null,
      productSku: item.ProductSku,
      purchaseOrderId: originalData.id,
      quantityContracted: item.QuantityContracted,
      quantityOrdered: item.QuantityOrdered,
      unitPrice: item.UnitPrice,
      useId: item.ProductCode?.includes("AZP") ? 2 : 1, // Tenta inferir se é professor pelo código
      deliveryTypeId: payload.DeliveryTypeId || 2, // Geralmente 2 para pedidos de 2º semestre
    }));

    // 3. Monta o objeto final
    return {
      id: originalData.id,
      orderNumber: originalData.orderNumber,
      clientName: originalData.clientName,
      clientCode: originalData.clientCode,
      couponCode: payload.CouponCode || null,
      couponDiscount: payload.CouponDiscount || 0.0,
      couponDiscountAmount: payload.CouponDiscountAmount || 0.0,
      createdAt: originalData.createdAt,
      status: originalData.status,
      portalB2BOrderStatus: "", // Campo vazio conforme exemplo
      subtotalAmount: payload.SubtotalAmount,
      totalAmount: payload.TotalAmount,
      totalItems: payload.TotalItems,
      shippingAmount: payload.ShippingAmount || 0.0,
      updatedAt: originalData.updatedAt,
      previsionDelivery: null,
      clientCnpj: payload.Customer.ClientCnpj,
      clientAddress: payload.Customer.ClientAddress,
      clientCity: payload.Customer.ClientCity,
      clientNumber: payload.Customer.ClientNumber,
      clientDistrict: payload.Customer.ClientDistrict,
      clientPostCode: payload.Customer.ClientPostCode,
      clientState: payload.Customer.ClientState,
      clientPhone: payload.Customer.ClientPhone || " +55",
      clientEmail: payload.Customer.ClientEmail,
      clientCompanyName: payload.Customer.ClientCompanyName,
      customer: {
        clientId: parseInt(originalData.clientId),
        clientAddress: payload.Customer.ClientAddress,
        clientCity: payload.Customer.ClientCity,
        clientNumber: payload.Customer.ClientNumber,
        clientDistrict: payload.Customer.ClientDistrict,
        clientComplement: payload.Customer.ClientComplement,
        clientPostCode: payload.Customer.ClientPostCode,
        clientCnpj: payload.Customer.ClientCnpj,
        clientCodeCnx: payload.Customer.ClientCodeCnx,
        clientCompanyName: payload.Customer.ClientCompanyName,
        clientCustomerGroup: payload.Customer.ClientCustomerGroup,
        clientEmail: payload.Customer.ClientEmail,
        clientFantasyName: payload.Customer.ClientFantasyName,
        clientPhone: payload.Customer.ClientPhone || " +55",
        clientSchoolIdLex: payload.Customer.ClientSchoolIdLex,
        clientState: payload.Customer.ClientState,
        shippingFee: payload.Customer.ShippingFee || 0,
        createdAt: payload.Customer.CreatedAt || originalData.createdAt,
        updatedAt: payload.Customer.UpdatedAt || originalData.updatedAt,
        quantityOfStudents: payload.Customer.QuantityOfStudents || 0,
        totalPurchaseOrderValue: payload.Customer.TotalPurchaseOrderValue || null,
      },
      quantityInstallment: 1, // Valor padrão (não encontrado no payload original)
      userEmail: originalData.createdBy,
      deliveryDate: originalData.scheduledDate,
      financialSituation: originalData.status || "pending",
      cycleCode: originalData.cycleCode,
      orderType: originalData.orderType || "Anual",
      isScheduled: true,
      items: items,
      deliveryTypeId: payload.DeliveryTypeId || 1,
    } as any;
  } catch (error) {
    console.error("Erro ao transformar pedido programado:", error, originalData);
    return {
      id: originalData.id,
      orderNumber: originalData.orderNumber,
      clientName: originalData.clientName,
      clientCode: originalData.clientCode,
      status: originalData.status,
      portalB2BOrderStatus: "",
      createdAt: originalData.createdAt,
      isScheduled: true,
      customer: {
        clientId: parseInt(originalData.clientId),
      },
      items: [],
    } as any;
  }
};

export const digitalProducts = [
  "1526001012877",
  "1526001012884",
  "1526001013416",
  "1526001013256",
  "1526001013263",
  "1526001013331",
  "1526001013348",
  "1526001013058",
  "1526001013096",
  "1526001013133",
  "1526001013171",
  "1526001013188",
  "1526001013010",
  "1526001012891",
  "1526001012907",
  "1526001013423",
  "1526001013027",
  "1526001013270",
  "1526001013287",
  "1526001013355",
  "1526001013362",
  "1526001013065",
  "1526001013102",
  "1526001013140",
  "1526001013195",
  "1526001013201",
  "1526001012914",
  "1526001012921",
  "1526001013430",
  "1526001013447",
  "1526001012938",
  "1526001012945",
  "1526001013294",
  "1526001013300",
  "1526001013379",
  "1526001013386",
  "1526001013218",
  "1526001013225",
  "1526001013072",
  "1526001013119",
  "1526001013157",
  "1526001012990",
  "1526001012976",
  "1526001012952",
  "1526001013003",
  "1526001012983",
  "1526001012969",
  "1526001013034",
  "1526001013041",
  "1526001013317",
  "1526001013324",
  "1526001013393",
  "1526001013409",
  "1526001013232",
  "1526001013249",
  "1526001013089",
  "1526001013126",
  "1526001013164",
];
